import React, { useEffect, useRef } from 'react';
import { motion, useAnimate, useScroll, useTransform, inView, stagger } from 'framer-motion';

import SectionColumns from '../../SectionBuilder/SectionColumns/SectionColumns';
import { MotionSection } from '../scrollAnimations';

// Custom cubic-bezier: smooth deceleration (similar to easeOutExpo)
const EASE_OUT = [0.22, 1, 0.36, 1];

/**
 * How it Works — static 4-column grid (Figma 249:128).
 * Console may still type this section as a carousel; we always render columns.
 *
 * Animation:
 *  - Section fades + slides up when it enters the viewport (whileInView).
 *  - A subtle vertical parallax is applied as the user scrolls through the section.
 *  - Each step block staggers in using framer-motion `useAnimate` + `inView`,
 *    which animates existing DOM nodes directly — no wrapper divs, so CSS
 *    direct-child selectors (icon size, text layout) remain unaffected.
 *
 * @component
 * @param {Object} props Page Builder section props
 * @returns {JSX.Element}
 */
const HowItWorksSection = props => {
  const { sectionId = 'how-it-works-section', options = {} } = props;

  // Ref for scroll-progress tracking (parallax)
  const parallaxRef = useRef(null);

  // Ref for useAnimate — scoped to the section content
  const [scope, animate] = useAnimate();

  // Scroll progress for the parallax: 0 when section enters viewport bottom,
  // 1 when section leaves viewport top.
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ['start end', 'end start'],
  });

  // Very subtle drift: +2% at entry → −2% at exit. Keeps the section feeling
  // alive without fighting with the SectionContainer's own positioning.
  const parallaxY = useTransform(scrollYProgress, [0, 1], ['2%', '-2%']);

  // Per-block stagger: animates existing DOM nodes, so no wrapper divs are
  // added and the CSS selectors in SectionBuilder.module.css stay intact.
  useEffect(() => {
    if (!scope.current) return;

    const cleanup = inView(
      scope.current,
      () => {
        // Blocks have ids like "<sectionId>-block-1", "<sectionId>-block-2" …
        const blocks = scope.current.querySelectorAll(`[id^="${sectionId}-block-"]`);
        if (!blocks.length) return;

        animate(
          Array.from(blocks),
          { opacity: [0, 1], y: [28, 0] },
          {
            delay: stagger(0.12, { startDelay: 0.15 }),
            duration: 0.65,
            ease: EASE_OUT,
          }
        );
      },
      { amount: 0.12 }
    );

    return cleanup;
    // animate/scope are stable references from useAnimate; sectionId never changes at runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId]);

  return (
    <MotionSection>
      {/*
        Outer motion.div: applies the continuous scroll parallax.
        The parallaxY MotionValue drives translateY throughout the user's scroll.
      */}
      <motion.div ref={parallaxRef} style={{ y: parallaxY }}>
        {/*
          Inner motion.div: one-shot reveal animation when the section enters
          the viewport. Uses `whileInView` so it respects prefers-reduced-motion
          via the MotionSection / MotionConfig wrapper above.
        */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.08 }}
          transition={{ duration: 0.75, ease: EASE_OUT }}
        >
          {/*
            scope ref: gives useAnimate a DOM root to query block IDs within.
            Plain div — no layout impact.
          */}
          <div ref={scope}>
            <SectionColumns {...props} sectionId={sectionId} numColumns={4} options={options} />
          </div>
        </motion.div>
      </motion.div>
    </MotionSection>
  );
};

export default HowItWorksSection;
